import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Text } from './Text';
import { cx } from '@/lib/cx';
import { colors } from '@/theme/tokens';
import styles from './AddValueSheet.module.css';

interface AddValueSheetProps {
  open: boolean;
  title: string;
  placeholder?: string;
  /** Prefills the field — the search text that came up empty, usually. */
  initial?: string;
  /**
   * Asks whether the new value is sold in halves. Units only: ໂຕນ and ຄິວ step
   * by 0.5 so a half load can be rung up, and the shop knows which of its own
   * units behave that way.
   */
  askFractional?: boolean;
  /** Returns a reason to refuse, or null. Used for "that already exists". */
  validate?: (value: string) => string | null;
  onAdd: (value: string, fractional: boolean) => void;
  onClose: () => void;
}

/**
 * Adds one value to a list the shop owns: a unit, a product category, an expense
 * category, a supplier.
 *
 * One sheet for all four, because they are the same interaction and four
 * near-identical dialogs would drift apart the way the two hard-coded category
 * lists already had.
 */
export function AddValueSheet({
  open,
  title,
  placeholder,
  initial = '',
  askFractional = false,
  validate,
  onAdd,
  onClose,
}: AddValueSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState('');
  const [fractional, setFractional] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setValue(initial);
      setFractional(false);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open, initial]);

  if (!open) return null;

  const trimmed = value.trim();
  const problem = trimmed ? (validate?.(trimmed) ?? null) : null;
  const canAdd = trimmed.length > 0 && problem === null;

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16}>
          {title}
        </Text>

        <Field
          value={value}
          onChange={setValue}
          placeholder={placeholder}
          label={title}
          autoFocus
          onEnter={() => canAdd && onAdd(trimmed, fractional)}
        />

        {problem ? (
          <Text size={11} weight={700} color={colors.skyDeep}>
            {problem}
          </Text>
        ) : null}

        {askFractional ? (
          <button
            type="button"
            role="switch"
            aria-checked={fractional}
            onClick={() => setFractional((v) => !v)}
            className={cx(styles.toggle, fractional && styles.toggleOn)}
          >
            <Text
              weight={700}
              size={12}
              color={fractional ? colors.surfaceAlt : colors.textMuted}
              align="left"
            >
              {fractional ? '✓ ' : ''}ຂາຍເປັນເສດໄດ້ (0.5)
            </Text>
            <Text
              size={10.5}
              color={fractional ? colors.onDarkBright : colors.textFaint}
              align="left"
            >
              ເຊັ່ນ ໂຕນ ຫຼື ຄິວ ທີ່ຂາຍເຄິ່ງໜຶ່ງໄດ້
            </Text>
          </button>
        ) : null}

        <div className={styles.actions}>
          <Button label="ຍົກເລີກ" variant="outline" className="grow" onPress={onClose} />
          <Button
            label="ເພີ່ມ"
            className="grow"
            disabled={!canAdd}
            onPress={() => onAdd(trimmed, fractional)}
          />
        </div>
      </div>
    </dialog>
  );
}
